import { useEffect, useState } from "react";
import { addDays, format } from "date-fns";
import { MessageCircle, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { api, waLink, fmtDate, errMsg } from "@/lib/api";
import { useCentro } from "@/lib/centro";

export const ProgramarWa = ({ busy, fecha, onProgramar, onCancelar }) => {
  const [val, setVal] = useState(format(addDays(new Date(), 1), "yyyy-MM-dd"));
  if (fecha) return (
    <div className="mt-2 flex flex-wrap items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1.5 text-emerald-900" data-testid="ppto-wa-programado-banner">
      <span>WhatsApp en la lista del {fmtDate(fecha)} (Panel principal).</span>
      <button type="button" onClick={onCancelar} data-testid="ppto-wa-cancelar" className="rounded border border-emerald-400 bg-white px-2 py-0.5">Quitar de la lista</button>
    </div>
  );
  return (
    <div className="flex flex-wrap items-center gap-2 mt-2" data-testid="ppto-wa-programar">
      <span className="text-slate-600">Dejar para enviar el día:</span>
      <input type="date" min={format(new Date(), "yyyy-MM-dd")} className="h-9 text-sm border border-slate-200 rounded px-2" value={val} onChange={(e) => setVal(e.target.value)} data-testid="ppto-wa-fecha" />
      <button type="button" disabled={busy || !val} onClick={() => onProgramar(val)} data-testid="ppto-wa-programar-btn" className="inline-flex items-center gap-1 rounded-md bg-[#25D366] text-white px-3 h-9 text-sm disabled:opacity-60"><CalendarClock className="h-4 w-4" />Programar WhatsApp</button>
    </div>
  );
};

const pdfFile = async (w) => {
  const { data } = await api.get(`/wa-programados/${w.id}/pdf`);
  const bin = Uint8Array.from(atob(data.pdf_base64), (c) => c.charCodeAt(0));
  return new File([bin], data.filename, { type: "application/pdf" });
};

export const WaPresupuestosHoy = () => {
  const { centro } = useCentro();
  const [list, setList] = useState([]);
  const [modo, setModo] = useState(false);
  const [file, setFile] = useState(null);
  useEffect(() => {
    api.get("/wa-programados", { params: { centro } }).then((r) => setList(r.data));
  }, [centro]);
  const cola = list.filter((w) => !w.hecho && w.patient.telefono);
  const sig = cola[0];
  useEffect(() => {
    setFile(null);
    if (modo && sig) pdfFile(sig).then(setFile);
  }, [modo, sig?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const enviar = async (w, f) => {
    try {
      const archivo = f || (await pdfFile(w));
      if (navigator.canShare?.({ files: [archivo] })) {
        await navigator.share({ files: [archivo], text: w.texto });
      } else {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(archivo);
        a.download = archivo.name;
        a.click();
        window.open(waLink(w.patient.telefono, w.texto), "_blank");
        toast.info("PDF descargado: en WhatsApp pulsa el clip y adjúntalo.");
      }
      await api.post(`/wa-programados/${w.id}/enviado`);
      setList((l) => l.map((x) => (x.id === w.id ? { ...x, hecho: true } : x)));
    } catch (e) {
      if (e?.name !== "AbortError") toast.error(errMsg(e));
    }
  };
  if (!list.length) return null;
  return (
    <div className="bg-white border border-emerald-200 rounded-lg p-4 mb-8 fade-up" data-testid="wa-presupuestos-hoy">
      <div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-800 mb-2">
        <MessageCircle className="h-4 w-4 text-[#25D366]" />WhatsApp de presupuestos para hoy
        <span className="ml-auto text-xs font-normal text-slate-500">{cola.length} por enviar de {list.length}</span>
        {cola.length > 0 && !modo && <button onClick={() => setModo(true)} data-testid="wa-ppto-todos-btn" className="rounded-md bg-[#25D366] text-white text-xs px-3 py-1">Enviar todos seguidos</button>}
      </div>
      {modo && (
        <div className="mb-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 flex flex-wrap items-center gap-2 text-sm" data-testid="wa-ppto-modo">
          {sig ? <>
            <span className="text-emerald-900">Siguiente: <b>{sig.patient.nombre}</b></span>
            <button onClick={() => enviar(sig, file)} disabled={!file} data-testid="wa-ppto-siguiente-btn" className="rounded-md bg-[#25D366] text-white px-3 py-1 text-sm disabled:opacity-60">{file ? "Abrir WhatsApp" : "Preparando PDF…"}</button>
            <span className="text-[11px] text-emerald-800">Envíalo, vuelve aquí y pulsa para el siguiente.</span>
          </> : <span className="text-emerald-900" data-testid="wa-ppto-fin">¡Listo! Todos los presupuestos están enviados.</span>}
          <button onClick={() => setModo(false)} data-testid="wa-ppto-salir-btn" className="ml-auto text-xs underline text-emerald-800">Terminar</button>
        </div>
      )}
      <div className="divide-y divide-slate-100">
        {list.map((w) => (
          <div key={w.id} className="flex items-center gap-3 py-1.5 text-sm" data-testid={`wa-ppto-${w.id}`}>
            <span className={`flex-1 truncate ${w.hecho ? "text-slate-400 line-through" : "text-slate-800"}`}>{w.patient.nombre} <span className="text-xs text-slate-400">· {w.patient.centro}{w.fecha < format(new Date(), "yyyy-MM-dd") ? ` · era para el ${fmtDate(w.fecha)}` : ""}</span></span>
            {w.hecho ? <span className="text-[11px] text-emerald-700">Enviado</span>
              : w.patient.telefono ? <button onClick={() => enviar(w)} data-testid={`wa-ppto-btn-${w.id}`} className="rounded px-2 py-0.5 text-[11px] text-white bg-[#25D366]">WhatsApp</button>
              : <span className="text-[11px] text-slate-400">sin teléfono</span>}
          </div>
        ))}
      </div>
    </div>
  );
};
